/*
 * Shared chrome for HVACloud AI task modals: hint textarea, indeterminate
 * progress with rotating status message, error area. The task body (result
 * view) and the action buttons live in the caller so schema/units/dashboard
 * can each render its own preview shape.
 *
 * Kept as a plain wrapper (no state) so the caller owns the state machine
 * (idle/loading/result/error) — that avoids a leaky prop surface.
 */

import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { AiConsole, type AiConsoleContext } from './AiConsole';
import '../hv-modal.css';
import './ai.css';

export type AiTaskStage = 'idle' | 'loading' | 'result' | 'applying' | 'error';

interface Props {
  title: string;
  description?: string;
  hint: string;
  onHintChange: (v: string) => void;
  hintPlaceholder: string;
  stage: AiTaskStage;
  errorMessage?: string | null;
  progressMessages: string[]; // rotate through these while stage === 'loading' or 'applying'
  children?: ComponentChildren; // result view (rendered when stage === 'result')
  footer: ComponentChildren;     // task-specific action buttons
  onClose: () => void;
  /** Optional hero illustration rendered at the very top of the modal body,
   *  above every stage. Caller centers via `.hv-modal__hero`. */
  hero?: ComponentChildren;
  /** Enables the interactive console above the hint textarea. Off-topic
   *  turns are refused server-side; the transcript is merged into the
   *  operator hint on Vorschlagen (parent handles the merge via a state
   *  echo — see the internal useState below). */
  chatContext?: AiConsoleContext;
  /** Extra content the caller wants shown above the hint textarea while the
   *  task is idle / error (e.g. a file dropzone, protocol picker). Hidden
   *  during loading / result stages so the operator sees progress or the
   *  finished proposal without the setup UI in the way. */
  idleExtra?: ComponentChildren;
}

export function AiTaskChrome({
  title,
  description,
  hint,
  onHintChange,
  hintPlaceholder,
  stage,
  errorMessage,
  progressMessages,
  children,
  footer,
  onClose,
  hero,
  chatContext,
  idleExtra,
}: Props) {
  const rotatingMsg = useRotatingMessage(progressMessages,
    stage === 'loading' || stage === 'applying');
  const elapsedSeconds = useElapsedSeconds(stage === 'loading' || stage === 'applying');
  // Chat transcript maintained separately from the hint so the operator can
  // still type into the hint textarea without it feeling clobbered. On
  // Vorschlagen the outer caller inspects `hint` — we prepend the transcript
  // there via onHintChange whenever it grows.
  const [chatHint, setChatHint] = useState('');
  const composedHint = chatHint.length > 0
    ? `${chatHint}${hint.trim() ? `\n\nZusätzlicher Freitext-Hinweis:\n${hint.trim()}` : ''}`
    : hint;
  // Keep the composed value in sync with the parent so it uses it on submit.
  // We store the raw operator-typed value locally and re-emit the composition.
  const [rawHint, setRawHint] = useState(hint);
  useEffect(() => {
    onHintChange(chatHint.length > 0
      ? `${chatHint}${rawHint.trim() ? `\n\nZusätzlicher Freitext-Hinweis:\n${rawHint.trim()}` : ''}`
      : rawHint);
  }, [chatHint, rawHint]);
  void composedHint;

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && stage !== 'loading' && stage !== 'applying') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, stage]);

  const busy = stage === 'loading' || stage === 'applying';

  return createPortal(
    <div class="hv-modal-backdrop" onClick={() => !busy && onClose()}>
      <div class="hv-modal hv-ai-modal" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">{title}</h2>
        </div>
        <div class="hv-modal__body">
          {hero && <div class="hv-modal__hero">{hero}</div>}
          {description && <p class="muted" style={{ marginTop: 0 }}>{description}</p>}

          {(stage === 'idle' || stage === 'error') && chatContext && (
            <AiConsole context={chatContext} onTranscriptChange={setChatHint} />
          )}

          {(stage === 'idle' || stage === 'error') && idleExtra}

          {(stage === 'idle' || stage === 'error') && (
            <div class="hv-modal__field">
              <label class="hv-ai-modal__hint-label" for="ai-hint">
                <HintIcon /> Zusätzlicher Hinweis (optional)
              </label>
              <textarea
                id="ai-hint"
                class="hv-ai-modal__hint-input"
                value={rawHint}
                onInput={(e) => setRawHint((e.target as HTMLTextAreaElement).value)}
                placeholder={hintPlaceholder}
              />
            </div>
          )}

          {busy && (
            <div class="hv-ai-progress" aria-live="polite">
              <RadialProgress elapsedSeconds={elapsedSeconds} maxSeconds={300} />
              <div class="hv-ai-progress__msg">{rotatingMsg}</div>
              {elapsedSeconds > 60 && (
                <div class="hv-ai-progress__hint">
                  Große Anlagen brauchen bis zu 5 Minuten — gedulde dich.
                </div>
              )}
            </div>
          )}

          {stage === 'result' && children}

          {stage === 'error' && errorMessage && (
            <p class="hv-ai-modal__error">{errorMessage}</p>
          )}
        </div>
        <div class="hv-modal__footer">{footer}</div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Wall-clock seconds since the given <c>active</c> flag flipped on. Resets
 * to 0 whenever it flips off. Used by the progress area to show operator
 * "job has been running for X seconds" — reassures that the wait is
 * expected on large plants.
 */
function useElapsedSeconds(active: boolean): number {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!active) { setSeconds(0); return; }
    const start = Date.now();
    setSeconds(0);
    const t = setInterval(() => setSeconds(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(t);
  }, [active]);
  return seconds;
}

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * SVG radial progress ring. Fill arc grows with elapsed/max (capped at 95%
 * so it never looks done before the call resolves). A separate 15%-arc
 * "spinner" rotates around the centre so the ring always reads as active
 * — even at 0s when the fill hasn't visibly moved yet. Wrapped in a <g>
 * with explicit-px transform-origin because SVG elements interpret
 * transform-origin percentages inconsistently across browsers.
 */
function RadialProgress({ elapsedSeconds, maxSeconds }: { elapsedSeconds: number; maxSeconds: number }) {
  const size = 72;
  const strokeWidth = 5;
  const cx = size / 2;
  const cy = size / 2;
  const radius = (size - strokeWidth) / 2; // 33.5, keeps the stroke inside the box
  const circumference = 2 * Math.PI * radius;
  const filled = Math.min(elapsedSeconds / maxSeconds, 0.95);
  const dashOffset = circumference * (1 - filled);
  const spinnerArc = circumference * 0.18;
  return (
    <div class="hv-ai-progress__ring" role="progressbar"
         aria-valuenow={Math.round(filled * 100)} aria-valuemin={0} aria-valuemax={100}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background ring — always visible. */}
        <circle
          cx={cx} cy={cy} r={radius}
          fill="none"
          stroke="rgba(124, 92, 255, 0.22)"
          stroke-width={strokeWidth}
        />
        {/* Fill arc — starts at 12 o'clock via a static rotate(-90) on this element. */}
        <circle
          cx={cx} cy={cy} r={radius}
          fill="none"
          stroke="#7c5cff"
          stroke-width={strokeWidth}
          stroke-linecap="round"
          stroke-dasharray={circumference}
          stroke-dashoffset={dashOffset}
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: 'stroke-dashoffset 900ms linear' }}
        />
        {/* Spinner arc — wraps in <g> so the CSS animation targets a group,
            not an SVG shape, and transform-origin is applied via the g's
            CSS box. */}
        <g class="hv-ai-progress__ring-spin" style={{ transformOrigin: `${cx}px ${cy}px` }}>
          <circle
            cx={cx} cy={cy} r={radius}
            fill="none"
            stroke="#c07bff"
            stroke-width={strokeWidth}
            stroke-linecap="round"
            stroke-dasharray={`${spinnerArc} ${circumference - spinnerArc}`}
          />
        </g>
      </svg>
      <div class="hv-ai-progress__ring-label">{formatElapsed(elapsedSeconds)}</div>
    </div>
  );
}

/**
 * Cycles through the given messages every ~2.4s while <c>active</c>. Resets
 * to the first message when active flips on so the operator always sees the
 * "starting" message first.
 */
function useRotatingMessage(messages: string[], active: boolean): string {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!active) { setIndex(0); return; }
    setIndex(0);
    const t = setInterval(() => setIndex((i) => (i + 1) % messages.length), 2400);
    return () => clearInterval(t);
  }, [active, messages.length]);
  return messages[Math.min(index, messages.length - 1)] ?? '';
}

function HintIcon() {
  return (
    <svg class="hv-ai-modal__hint-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="10" cy="10" r="7" />
      <path d="M10 6.5v4M10 13.6h.01" />
    </svg>
  );
}

